<script>
  import { onMount } from 'svelte';
  import { isAuthenticated, userRoles } from '$stores/sessionStore';
  import { navigate as goto } from 'svelte-routing';
  import BarChart from './cgmCompliance/BarChart.svelte';
  import DailyBarChart from './cgmCompliance/DailyBarChart.svelte';
  import MilestoneDailyBarChart from './cgmCompliance/MilestoneDailyBarChart.svelte';
  import ParticipantDetails from '$lib/ParticipantDetails.svelte';
  import FeatureInfo from '$lib/FeatureInfo.svelte';
  import Pagination from '$lib/Pagination.svelte';
  import { selectedStudy, selectedSite, selectedParticipant } from '$stores/selectionStore';
  import Toolbar from '$lib/Toolbar.svelte';
  import Footer from '$lib/Footer.svelte';
  import StudyHeader from '$lib/StudyHeader.svelte';
  import { studyProperties } from '$stores/configStore';
  import { FontAwesomeIcon } from '@fortawesome/svelte-fontawesome';
  import { faChevronUp, faChevronDown, faRotate } from '@fortawesome/pro-light-svg-icons';
  
  // Use local API endpoints instead of direct API with key
  const API_BASE_URL = '/api/proxy';

  import { exportCGMSensorLogToCSV, exportToCSV, exportToExcel, generateExportFilename } from '$utils/export.js';
  import { formatToLocalDateTime } from '$utils/dateUtils.js';

  let compliances = [];
  let compliancesCount = 0;
  let currentPage = 1;
  let totalPages = 1;
  let limit = 20;
  let chartCompliances = [];
  let dailyCompliances = [];
  let isLoading = true;
  let dataDebug = '';

  // SPTL-492: legacy parity — single mutually-exclusive View dropdown instead
  // of two independent chart-toggle buttons. Modes: table | hourly | daily | visit.
  let displayMode = 'table';
  let visitActivities = [];
  let selectedVisitId = '';
  let visitChartAnchor = 'planned';
  // Alert overlays on the Visit chart require schedulerJobConfig data. Default
  // off; populated by fetchAlertJobConfigs when study + participant resolved.
  let participantAlertJobConfig = null;
  let siteAlertJobConfig = null;
  let showVisitAlertSchedule = false;
  const PARTICIPANT_CGM_ALERT_JOB_ID = 'subject-activity-cgm-compliance';
  const SITE_CGM_ALERT_JOB_ID = 'subject-activity-cgm-site-compliance';

  $: selectedVisit =
    selectedVisitId && visitActivities.find((a) => String(a._id) === String(selectedVisitId));
  $: if (visitActivities.length > 0 && !selectedVisitId) {
    selectedVisitId = String(visitActivities[0]._id);
  }

  // SPTL-492: legacy parity — only admin/support/superadmin see the
  // theoretical-alert-schedule overlay button on the Visit chart. Non-admin
  // users shouldn't see "what would have alerted" details.
  $: canViewVisitAlertSchedule = ($userRoles || []).some((role) =>
    ['admin', 'support', 'superadmin'].includes(String(role).toLowerCase())
  );
  // If the user loses the role (or never had it), force the toggle off so
  // the overlay disappears immediately.
  $: if (!canViewVisitAlertSchedule) {
    showVisitAlertSchedule = false;
  }

  async function fetchCompliances(page = 1) {
    try {
      isLoading = true;
      const res = await fetch(
        `${API_BASE_URL}/glucoseReadings/daily?page=${page}&pageSize=${limit}&subjectGUID=${$selectedParticipant._id}&type=CGM&timezone=${$selectedParticipant.timezoneCode}`
      );

      if (!res.ok) {
        throw new Error('Failed to fetch cgm compliance');
      }

      const data = await res.json();
      //console.info("data: ", data);

      compliances = data.data || []; // Ensure compliances is an array
      compliancesCount = data.totalCount || 0;
      currentPage = data.page || 1;
      totalPages = data.totalPages || 1;

      // Also use this data for the daily chart
      if (compliances.length > 0 && page === 1) {
        dailyCompliances = [...compliances];
        dataDebug = `Daily data: ${dailyCompliances.length} items`;
      }
    } catch (err) {
      console.error('Error fetching compliance:', err);
      compliances = []; // Set to empty array in case of error
      dataDebug = `Error: ${err.message}`;
    } finally {
      isLoading = false;
    }
  }

  async function fetchChartCompliances() {
    try {
      const res = await fetch(
        `${API_BASE_URL}/glucoseReadings/hourlyChart?page=1&pageSize=1344&subjectGUID=${$selectedParticipant._id}&type=CGM&timezone=${$selectedParticipant.timezoneCode}`
      );

      if (!res.ok) {
        throw new Error('Failed to fetch cgm compliance');
      }

      const data = await res.json();
      chartCompliances = data.data || [];
      dataDebug += ` | Hourly data: ${chartCompliances.length} items`;
      //chartCompliances = [...chartCompliances].reverse();
    } catch (err) {
      console.error('Error fetching compliance:', err);
      chartCompliances = []; // Set to empty array in case of error
      dataDebug += ` | Hourly Error: ${err.message}`;
    }
  }

  // Fetch ALL compliance data for the charts
  async function fetchAllCompliances() {
    try {
      // First request to get total count
      const countRes = await fetch(
        `${API_BASE_URL}/glucoseReadings/daily?page=1&pageSize=20&subjectGUID=${$selectedParticipant._id}&type=CGM&timezone=${$selectedParticipant.timezoneCode}`
      );
      
      if (!countRes.ok) {
        throw new Error('Failed to get total count for cgm compliance');
      }
      
      const countData = await countRes.json();
      const totalCount = countData.totalCount || 0;
      const totalPages = countData.totalPages || 1;
      
      // console.log(`Found ${totalCount} total days of compliance data across ${totalPages} pages`);
      
      // Try to get all data in one request first
      const pageSize = Math.max(100, totalCount);
      
      const res = await fetch(
        `${API_BASE_URL}/glucoseReadings/daily?page=1&pageSize=${pageSize}&subjectGUID=${$selectedParticipant._id}&type=CGM&timezone=${$selectedParticipant.timezoneCode}`
      );

      if (!res.ok) {
        throw new Error('Failed to fetch all cgm compliance');
      }

      const result = await res.json();
      let allData = [];

      // Assuming the API returns an object with a 'data' property that's an array
      if (result && Array.isArray(result.data)) {
        allData = [...result.data];
        
        // If we didn't get all data in one go, fetch additional pages if needed
        if (allData.length < totalCount && result.totalPages > 1) {
          // console.log(`Need to fetch more pages. Got ${allData.length} of ${totalCount} items`);
          
          // Fetch remaining pages (max 5 to avoid too many requests)
          const maxAdditionalPages = Math.min(4, result.totalPages - 1);
          
          for (let page = 2; page <= 1 + maxAdditionalPages; page++) {
            try {
              const pageRes = await fetch(
                `${API_BASE_URL}/glucoseReadings/daily?page=${page}&pageSize=${pageSize}&subjectGUID=${$selectedParticipant._id}&type=CGM&timezone=${$selectedParticipant.timezoneCode}`
              );
              
              if (pageRes.ok) {
                const pageData = await pageRes.json();
                if (pageData && Array.isArray(pageData.data)) {
                  allData = [...allData, ...pageData.data];
                  console.log(`Added page ${page} data, now have ${allData.length} items`);
                }
              }
            } catch (pageErr) {
              console.error(`Error fetching page ${page}:`, pageErr);
            }
            
            // Stop if we have all the data
            if (allData.length >= totalCount) break;
          }
        }
        
        // Update dailyCompliances with all fetched data
        dailyCompliances = allData;
        //console.log(`Successfully fetched ${dailyCompliances.length} days of data out of ${totalCount} total days`);
        dataDebug += ` | All data: ${dailyCompliances.length} items`;
        
        // if (dailyCompliances.length > 0) {
        //   // Log a sample data point to check the structure
        //   console.log("Sample daily compliance item:", dailyCompliances[0]);
        // }
        
        return dailyCompliances;
      } else {
        console.error('API response does not contain a data array:', result);
        dataDebug += ` | All Error: No data array`;
        return [];
      }
    } catch (err) {
      console.error('Error fetching all compliance:', err);
      dataDebug += ` | All Error: ${err.message}`;
      return [];
    }
  }

  // SPTL-492: legacy ports — fetch milestone visit activities + scheduler job
  // configs so the Visit Chart dropdown option has data + (optional) alert
  // overlays. Both gated on participant + study selection respectively.
  async function fetchVisitActivities() {
    if (!$selectedParticipant?._id) {
      visitActivities = [];
      selectedVisitId = '';
      return;
    }
    try {
      const res = await fetch(
        `${API_BASE_URL}/subjectSchedule/subject-activities?subjectGUID=${$selectedParticipant._id}&pageSize=999`
      );
      if (!res.ok) throw new Error('Failed to fetch subject activities');
      const response = await res.json();
      const activities = Array.isArray(response.data) ? response.data : [];
      visitActivities = activities
        .filter((a) => a.activityTypeCode === 'MILE')
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
      if (
        selectedVisitId &&
        !visitActivities.some((a) => String(a._id) === String(selectedVisitId))
      ) {
        selectedVisitId = '';
      }
    } catch (err) {
      console.error('Error fetching visit activities:', err);
      visitActivities = [];
    }
  }

  async function fetchSchedulerJobConfig(jobId) {
    if (!$selectedStudy?._id) return null;
    try {
      const params = new URLSearchParams({ studyGUID: $selectedStudy._id });
      const res = await fetch(`${API_BASE_URL}/schedulerJobConfigs/${jobId}?${params}`);
      if (!res.ok) return null;
      const body = await res.json();
      return body?.data || body || null;
    } catch (err) {
      console.error(`Error fetching scheduler job config for ${jobId}:`, err);
      return null;
    }
  }

  async function fetchAlertJobConfigs() {
    const studyId = $selectedStudy?._id;
    if (!studyId) {
      participantAlertJobConfig = null;
      siteAlertJobConfig = null;
      return;
    }
    const [participantConfig, siteConfig] = await Promise.all([
      fetchSchedulerJobConfig(PARTICIPANT_CGM_ALERT_JOB_ID),
      fetchSchedulerJobConfig(SITE_CGM_ALERT_JOB_ID)
    ]);
    if (($selectedStudy?._id || '') !== studyId) return;
    participantAlertJobConfig = participantConfig;
    siteAlertJobConfig = siteConfig;
  }

  // Trigger Visit-chart data fetches when participant changes.
  $: if ($selectedParticipant?._id) {
    fetchVisitActivities();
  } else {
    visitActivities = [];
    selectedVisitId = '';
  }
  // SPTL-492: don't auto-fetch alert configs on study change — they 404
  // until the backend seeds the schedulerJobConfig docs. Lazy-load on first
  // toggle-on of Show alerts (see toggle handler below).
  let alertConfigStudyId = '';

  async function toggleVisitAlertSchedule() {
    const next = !showVisitAlertSchedule;
    if (next && canViewVisitAlertSchedule) {
      // Lazy-fetch on first enable for the current study; skip refetch if same.
      const currentStudyId = $selectedStudy?._id || '';
      if (currentStudyId && currentStudyId !== alertConfigStudyId) {
        await fetchAlertJobConfigs();
        alertConfigStudyId = currentStudyId;
      }
    }
    showVisitAlertSchedule = next;
  }

  onMount(() => {
    if ($selectedParticipant) {
      fetchCompliances(1);
      fetchChartCompliances();
      fetchAllCompliances();
    } else {
      isLoading = false;
    }
  });

  async function handleExportCSV() {
    const allCompliances = await fetchAllCompliances();
    exportCGMSensorLogToCSV(
      allCompliances,
      $selectedStudy.protocolNumber + '_' + $selectedParticipant.subjectId + '_Compliance',
      'NONE',
      $selectedParticipant.subjectId
    );
    //exportToCSV(allCompliances, "compliances");
  }

  async function handleExportExcel() {
    const allCompliances = await fetchAllCompliances();
    
    // Transform data to match table column order
    const expectedPerDay = Math.round(1440 / (($studyProperties?.glucoseParameters?.cgmIntervalMinutes ?? 5)));
    const exportData = allCompliances.map(compliance => ({
      'Day (Local)': compliance.day,
      'Serial Nr': compliance.externalId,
      'Location': compliance.location,
      'Count': compliance.totalCount,
      'Percent': `${((compliance.totalCount / expectedPerDay) * 100).toFixed(1)}%`,
      'First (Local)': formatToLocalDateTime(compliance.minTimestamp, $selectedParticipant.timezoneCode),
      'Last (Local)': formatToLocalDateTime(compliance.maxTimestamp, $selectedParticipant.timezoneCode)
    }));
    
    const filename = generateExportFilename({
      protocolNumber: $selectedStudy?.protocolNumber,
      subjectId: $selectedParticipant?.subjectId,
      reportType: 'CGM_Compliance'
    });
    
    exportToExcel(exportData, filename);
  }

  function handlePageChange(newPage) {
    if (newPage > 0 && newPage <= totalPages) {
      fetchCompliances(newPage);
    }
  }

  function handleRefresh() {
    fetchCompliances(1);
    fetchChartCompliances();
    fetchAllCompliances();
  }

  function handleChartVisibility() {
    // Toggle hourly chart, hide daily chart if we're showing hourly
    chartVisible = !chartVisible;
    if (chartVisible) {
      dailyChartVisible = false; // Hide daily chart when showing hourly
    }
  }
  
  function handleDailyChartVisibility() {
    // Toggle daily chart, hide hourly chart if we're showing daily
    dailyChartVisible = !dailyChartVisible;
    if (dailyChartVisible) {
      chartVisible = false; // Hide hourly chart when showing daily
    }
  }
</script>

<StudyHeader />

<FeatureInfo 
  title="CGM Compliance" 
  description="This page displays continuous glucose monitoring (CGM) data compliance for the selected participant. You can view hourly and daily readings patterns, explore detailed data by day, and export the data for further analysis."
  marginBottom="mb-0"
/>

{#if $selectedParticipant}
  <ParticipantDetails showOptionsMenu={false} />
{/if}

<Toolbar title="CGM Compliance" itemCountTitle="Days" itemCount={displayMode === 'table' ? compliancesCount : null}>
  <div class="flex items-center gap-2 flex-wrap">
    <label for="cgm-display-mode" class="text-xs text-gray-700 dark:text-slate-300 whitespace-nowrap">View</label>
    <select
      id="cgm-display-mode"
      bind:value={displayMode}
      class="text-sm rounded border border-stone-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-slate-100 px-2 py-1 min-w-[12rem]"
    >
      <option value="table">Daily Counts</option>
      <option value="hourly">Hourly Chart</option>
      <option value="daily">Daily Chart</option>
      <option value="visit">Visit Chart</option>
    </select>
    {#if displayMode === 'visit' && visitActivities.length > 0 && $selectedParticipant}
      <label for="cgm-visit-select" class="text-xs text-gray-700 dark:text-slate-300 whitespace-nowrap">Visit</label>
      <select
        id="cgm-visit-select"
        bind:value={selectedVisitId}
        class="text-sm rounded border border-stone-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-slate-100 px-2 py-1 min-w-[14rem]"
      >
        {#each visitActivities as ma (ma._id)}
          <option value={String(ma._id)}>{ma.name || ma.activityTypeName || 'Visit'}</option>
        {/each}
      </select>
      <label for="cgm-visit-center" class="text-xs text-gray-700 dark:text-slate-300 whitespace-nowrap">Center on</label>
      <select
        id="cgm-visit-center"
        bind:value={visitChartAnchor}
        class="text-sm rounded border border-stone-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-slate-100 px-2 py-1"
      >
        <option value="planned">Planned</option>
        <option value="started">Started</option>
        <option value="visitDate">Visit Date</option>
      </select>
      <!-- SPTL-492: Show/Hide alerts toggle — flips the theoretical alert
           schedule overlay on the Visit chart. Role-gated (admin/support/
           superadmin only — non-admin shouldn't see what-would-have-alerted
           data). Fetches alert configs lazily on first enable. -->
      {#if canViewVisitAlertSchedule}
        <button
          type="button"
          on:click={toggleVisitAlertSchedule}
          class="pagination ml-2 px-3 py-1 text-sm rounded border border-stone-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-slate-100 hover:bg-stone-100 dark:hover:bg-gray-700"
        >
          {showVisitAlertSchedule ? 'Hide alerts' : 'Show alerts'}
        </button>
      {/if}
    {/if}
    <button on:click={handleRefresh} class="pagination ml-2 p-0 text-s" title="Refresh data">
      <FontAwesomeIcon icon={faRotate} />
    </button>
  </div>
</Toolbar>

{#if isLoading}
  <div class="flex flex-col justify-center items-center h-64 bg-stone-100/50 dark:bg-gray-725 mx-1">
    <div class="loader mb-4"></div>
    <p class="text-xs justify-center text-gray-700 dark:text-gray-300">
      preparing CGM compliance data
    </p>
  </div>
{:else}
  {#if displayMode === 'hourly'}
    <!-- Hourly Bar Chart -->
    {#if chartCompliances.length > 0}
      <div class="flex pb-5 mt-4">
        <div class="w-full">
          <h3 class="text-sm font-medium ml-2 mb-1 text-gray-800 dark:text-slate-300">Hourly Readings</h3>
          <BarChart data={chartCompliances} />
        </div>
      </div>
    {/if}
  {/if}

  {#if displayMode === 'daily'}
    <!-- Daily Bar Chart -->
    {#if dailyCompliances.length > 0}
      <div class="flex pb-5 mt-4">
        <div class="w-full">
          <h3 class="text-sm font-medium ml-2 mb-1 text-gray-800 dark:text-slate-300">
            Daily Readings
          </h3>
          <DailyBarChart data={dailyCompliances} />
        </div>
      </div>
    {:else}
      <div class="text-sm ml-2 mb-2 text-gray-600 dark:text-gray-400">
        No daily compliance data available.
      </div>
    {/if}
  {/if}

  {#if displayMode === 'visit'}
    <!-- Milestone Visit Chart (SPTL-492 port of legacy MilestoneDailyBarChart) -->
    <div class="flex pb-5 mt-4">
      <div class="w-full ml-2">
        {#if visitActivities.length === 0}
          <p class="text-xs text-gray-500 dark:text-gray-500">
            No visit activities (type MILE) found for this participant.
          </p>
        {:else if selectedVisit}
          {#key `${selectedVisitId}-${visitChartAnchor}`}
            <MilestoneDailyBarChart
              data={dailyCompliances}
              visit={selectedVisit}
              timezone={$selectedParticipant?.timezoneCode || 'UTC'}
              anchorMode={visitChartAnchor}
              participantAlertJobConfig={participantAlertJobConfig}
              siteAlertJobConfig={siteAlertJobConfig}
              showAlertSchedule={showVisitAlertSchedule}
            />
          {/key}
        {/if}
      </div>
    </div>
  {/if}

  {#if displayMode === 'table'}
  <div class="px-1 pt-0">
    <table class="min-w-full border-collapse table-auto">
      <thead>
        <tr class="text-sm text-black dark:text-slate-300 bg-stone-200/50 dark:bg-gray-800/90">
          <th class="px-4 py-2 text-left bold">Day (Local)</th>
          <th class="px-4 py-2 text-left bold">Serial Nr</th>
          <th class="px-4 py-2 text-left bold">Location</th>
          <th class="px-4 py-2 text-left bold">Count</th>
          <th class="px-4 py-2 text-left bold">Percent</th>
          <th class="px-4 py-2 text-left bold">First (Local)</th>
          <th class="px-4 py-2 text-left bold">Last (Local)</th>
        </tr>
      </thead>
      <tbody>
        {#each compliances as compliance, index}
          <tr
            class="text-sm hover:bg-white dark:hover:bg-gray-775 text-gray-800 dark:text-slate-300 {index %
              2 ===
            0
              ? 'bg-stone-50 dark:bg-gray-725'
              : 'bg-stone-75 dark:bg-gray-750'}"
          >
            <td
              class="px-4 py-2 cursor-pointer"
              on:click={() => goto(`/logEntries?date=${compliance.day}`)}>{compliance.day}</td
            >
            <td class="px-4 py-2">{compliance.externalId}</td>
            <td class="px-4 py-2">{compliance.location ?? ''}</td>
            <td class="px-4 py-2">{compliance.totalCount}</td>
            <td class="px-4 py-2">
              {#if $studyProperties}
                {(() => {
                  const expectedPerDay = Math.round(1440 / (($studyProperties?.glucoseParameters?.cgmIntervalMinutes ?? 5)));
                  return ((compliance.totalCount / expectedPerDay) * 100).toFixed(1) + '%';
                })()}
              {:else}
                {((compliance.totalCount / 288) * 100).toFixed(1)}%
              {/if}
            </td>
            <td class="px-4 py-2"
              >{formatToLocalDateTime(compliance.minTimestamp, $selectedParticipant.timezoneCode)}</td
            >
            <td class="px-4 py-2"
              >{formatToLocalDateTime(compliance.maxTimestamp, $selectedParticipant.timezoneCode)}</td
            >
          </tr>
        {/each}
      </tbody>
    </table>
  </div>
  {/if}
{/if}

<!-- SPTL-537: pagination + Excel export belong to the Daily Counts (table) view
     only. The chart views (Hourly/Daily/Visit) have no rows to page or export,
     so gate the footer on displayMode === 'table' for Pre-Security Revamp parity. -->
{#if displayMode === 'table'}
<!-- Pagination and Export Buttons -->
<Footer>
  <Pagination {currentPage} {totalPages} onPageChange={handlePageChange} />
  <div>
    <button hidden
      on:click={handleExportCSV}
      class="bg-stone-300 dark:bg-gray-600 text-black dark:text-white py-1 px-4 rounded-full text-xs"
      >CSV</button
    >
    <button
      on:click={handleExportExcel}
      class="bg-stone-300 dark:bg-gray-600 text-black dark:text-white py-1 px-4 rounded-full text-xs"
      >Excel</button
    >
  </div>
</Footer>
{/if}

<style>
  .loader {
    border: 2px solid #20669c;
    border-radius: 50%;
    border-top: 4px solid #98acb4;
    width: 32px;
    height: 32px;
    animation: spin 2s linear infinite;
  }

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
</style>
